import { useRef, useState, useEffect } from 'react';
import {
    Container,
    Card,
    CardMedia,
    Typography,
    Button,
    Avatar,
    Box,
    Chip,
    IconButton,
    useMediaQuery,
    useTheme,
    styled
} from '@mui/material';
import {
    CalendarToday,
    LocationOn,
    Star,
    Storefront,
    NavigateNext,
    NavigateBefore,
    ArrowForward
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { managementCourse } from '../../context/ManagementCourseData';
import { useLanguage } from '../../context/LanguageContext';

// Luxurious color palette
const primaryColor = '#FF6D00';
const secondaryColor = '#FF9100';
const darkOrange = '#E65100';
const bgColor = '#FFF8F2';
const goldAccent = '#FFD700';
const darkText = '#2D2D2D';
const lightText = '#5A5A5A';

// Styled components
const SectionHeader = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(0, 1),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1)
    }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '1.5rem',
    color: darkText,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.3rem'
    }
}));

const ViewAllButton = styled(Button)(({ theme }) => ({
    position: 'absolute',
    right: theme.spacing(1),
    top: 0,
    fontWeight: 500,
    color: primaryColor,
    borderColor: 'rgba(255, 109, 0, 0.2)',
    borderRadius: '20px',
    padding: theme.spacing(0.5, 2),
    fontSize: '0.75rem',
    textTransform: 'none',
    '&:hover': {
        borderColor: darkOrange,
        backgroundColor: 'rgba(255, 109, 0, 0.05)'
    },
    [theme.breakpoints.down('sm')]: {
        position: 'relative',
        alignSelf: 'flex-end',
        marginTop: theme.spacing(0.5)
    }
}));

const CarouselContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    marginBottom: theme.spacing(6),
    overflow: 'hidden',
    padding: theme.spacing(0, 1)
}));

const CarouselTrack = styled(Box)(({ theme }) => ({
    display: 'flex',
    transition: 'transform 0.5s ease',
    gap: theme.spacing(3),
    scrollBehavior: 'smooth',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
        display: 'none'
    },
    [theme.breakpoints.down('md')]: {
        gap: theme.spacing(2)
    }
}));

const CarouselItem = styled(Box)(({ theme }) => ({
    minWidth: `calc(33.333% - ${theme.spacing(3)})`,
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
        minWidth: `calc(50% - ${theme.spacing(2)})`
    },
    [theme.breakpoints.down('sm')]: {
        minWidth: '100%'
    }
}));

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    },
}));

const CourseMedia = styled(CardMedia)({
    height: '180px',
    position: 'relative',
});

const NavButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'white',
    color: primaryColor,
    boxShadow: theme.shadows[4],
    width: 40,
    height: 40,
    zIndex: 10,
    '&:hover': {
        backgroundColor: bgColor,
        transform: 'translateY(-50%) scale(1.1)'
    },
    [theme.breakpoints.down('md')]: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
    }
}));

const PrevButton = styled(NavButton)(({ theme }) => ({
    left: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        left: theme.spacing(0.5)
    }
}));

const NextButton = styled(NavButton)(({ theme }) => ({
    right: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        right: theme.spacing(0.5)
    }
}));

const BusinessManagement = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t, language } = useLanguage(); // Make sure to destructure language
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Refs for carousel containers
    const recordedRef = useRef(null);
    const onlineRef = useRef(null);
    const offlineRef = useRef(null);

    // State to track current positions
    const [positions, setPositions] = useState({
        recorded: 0,
        online: 0,
        offline: 0
    });

    // Calculate items per view based on screen size
    const itemsPerView = isMobile ? 1 : isTablet ? 2 : 3;

    // Organize managementCourse by type
    const workshopTypes = {
        recorded: managementCourse.filter(course => course.type === 'recorded'),
        online: managementCourse.filter(course => course.type === 'online'),
        offline: managementCourse.filter(course => course.type === 'offline')
    };


    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const handleViewAll = (type) => {
        navigate('/workshop/detail', {
            state: {
                filterType: type,
                title: t(`workshopPage.${type}Title`),
                filteredCourses: workshopTypes[type]
            }
        });
    };

    const handleScroll = (type, direction) => {
        const container = type === 'recorded' ? recordedRef.current :
            type === 'online' ? onlineRef.current : offlineRef.current;
        const items = workshopTypes[type];
        const itemWidth = container.firstChild?.offsetWidth || 0;
        const gap = parseInt(window.getComputedStyle(container).gap);

        let newPosition = positions[type] + direction;

        if (newPosition < 0) {
            newPosition = Math.ceil(items.length / itemsPerView) - 1;
        } else if (newPosition >= Math.ceil(items.length / itemsPerView)) {
            newPosition = 0;
        }

        setPositions(prev => ({ ...prev, [type]: newPosition }));

        container.scrollTo({
            left: newPosition * (itemWidth + gap) * itemsPerView,
            behavior: 'smooth'
        });
    };

    const renderBadge = (type) => {
        const badgeProps = {
            sx: {
                position: 'absolute',
                top: 12,
                right: 12,
                color: 'white',
                fontWeight: '600',
                fontSize: '0.7rem'
            }
        };

        switch (type) {
            case 'online':
                return <Chip label={t('workshopPage.virtualBadge')} {...badgeProps} sx={{ ...badgeProps.sx, backgroundColor: '#4CAF50' }} />;
            case 'offline':
                return <Chip label={t('workshopPage.inPersonBadge')} {...badgeProps} sx={{ ...badgeProps.sx, backgroundColor: primaryColor }} />;
            default:
                return <Chip label={t('workshopPage.recordedBadge')} {...badgeProps} sx={{ ...badgeProps.sx, backgroundColor: '#2196F3' }} />;
        }
    };

    const renderBusinessManagement = (course) => (
        <StyledCard>
            <CourseMedia image={course.image} title={course.title}>
                <Box sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: '4px 10px',
                    borderRadius: '16px',
                    fontWeight: '700',
                    color: darkOrange,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    {course.discount ? (
                        <>
                            <span style={{ textDecoration: 'line-through', opacity: 0.7, fontSize: '0.7rem' }}>
                                {course.price}
                            </span>
                            <span style={{ color: '#E53935', fontWeight: '800', fontSize: '0.9rem' }}>
                                {course.discountedPrice}
                            </span>
                        </>
                    ) : (
                        <span style={{ fontSize: '0.9rem' }}>{course.price}</span>
                    )}
                </Box>
                {renderBadge(course.type)}
            </CourseMedia>

            <Box sx={{ p: 1.5, flexGrow: 1 }}>
                <Typography gutterBottom variant="h6" component="h3" sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: darkText,
                    height: '3.2em',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    fontSize: isMobile ? '0.95rem' : '1rem'
                }}>
                    {course.title}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar src={course.instructor.avatar} alt={course.instructor.name} sx={{
                        width: 30,
                        height: 30,
                        mr: 1.5,
                        border: `2px solid ${primaryColor}`
                    }} />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="600" color={darkText} sx={{ fontSize: isMobile ? '0.8rem' : '0.85rem' }}>
                            {course.instructor.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Star fontSize="small" sx={{ color: goldAccent, mr: 0.5, fontSize: '0.8rem' }} />
                            <Typography variant="caption" color={lightText} sx={{ fontSize: '0.75rem' }}>
                                {course.instructor.rating}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {course.type !== 'recorded' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <CalendarToday color="primary" fontSize="small" sx={{ mr: 1, fontSize: '0.8rem' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                            {!isNaN(Date.parse(course.data)) ? new Date(course.date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                            }) : 'Coming Soon'}
                        </Typography>
                    </Box>
                )}

                {course.type === 'offline' && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOn color="primary" fontSize="small" sx={{ mr: 1, fontSize: '0.8rem' }} />
                        <Typography variant="caption" sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.75rem'
                        }}>
                            {course.venue}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ p: 1.5, pt: 0 }}>
                <Button
                    variant="contained"
                    fullWidth
                    href={course.registrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        py: 0.75,
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        background: `linear-gradient(45deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        '&:hover': {
                            background: `linear-gradient(45deg, ${darkOrange} 0%, ${primaryColor} 100%)`,
                        }
                    }}
                >
                    {course.type === 'recorded' ? t('workshopPage.watchNow') : t('workshopPage.registerNow')}
                    <ArrowForward sx={{ ml: 1, fontSize: '1rem' }} />
                </Button>
            </Box>
        </StyledCard>
    );

    return (
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, mt: 0 }}>
            <Box sx={{
                position: 'relative',
                width: '100vw',
                left: '50%',
                transform: 'translateX(-50%)',
                height: { xs: '180px', sm: '280px', md: '450px' },
                overflow: 'hidden',
                marginBottom: 4,
                marginTop: 0,
            }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.1), rgba(0,0,0,0.2))', zIndex: 1 }} />
                <Box
                    component="img"
                    src="/images/skillset/consultant.png"
                    alt="Luxury Beauty Education"
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        display: 'block'
                    }}
                />
                <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: { xs: 2, sm: 3, md: 4 },
                    color: 'white',
                    zIndex: 2,
                    maxWidth: '1200px',
                    margin: '0 auto',
                }}>
                    <Typography variant="h2" component="h1" gutterBottom sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.6rem', sm: '2rem', md: '2.2rem' },
                        lineHeight: 1.2,
                        textShadow: '1px 1px 3px rgba(0,0,0,0.5)'
                    }}>
                        {t('workshopPage.masterTitle')}
                    </Typography>
                    <Typography variant="h5" sx={{
                        maxWidth: { xs: '100%', sm: '70%', md: '60%' },
                        fontWeight: 300,
                        fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.2rem' },
                        mt: 1,
                        textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                    }}>
                        {t('workshopPage.masterSubtitle')}
                    </Typography>
                </Box>
            </Box>


            {/* Offline BusinessManagements Section */}
            <Box sx={{ mb: 4, position: 'relative' }}>
                <SectionHeader>
                    <SectionTitle>
                        <Storefront fontSize="small" />
                        {t('workshopPage.offlineTitle')}
                    </SectionTitle>
                    {workshopTypes.offline.length > 0 &&
                        <ViewAllButton
                            variant="outlined"
                            onClick={() => handleViewAll('offline')}
                        >
                            {t('workshopPage.viewAll')}
                        </ViewAllButton>
                    }
                </SectionHeader>

                <CarouselContainer>
                    <CarouselTrack ref={offlineRef}>
                        {workshopTypes.offline.map((course, index) => (
                            <CarouselItem key={`offline-${course.id}-${index}`}>
                                {renderBusinessManagement(course)}
                            </CarouselItem>
                        ))}
                    </CarouselTrack>
                    {workshopTypes.offline.length > itemsPerView && (
                        <>
                            <PrevButton
                                onClick={() => handleScroll('offline', -1)}
                                aria-label={t('workshopPage.previous')}
                            >
                                <NavigateBefore fontSize="small" />
                            </PrevButton>
                            <NextButton
                                onClick={() => handleScroll('offline', 1)}
                                aria-label={t('workshopPage.next')}
                            >
                                <NavigateNext fontSize="small" />
                            </NextButton>
                        </>
                    )}
                </CarouselContainer>
            </Box>
        </Container>
    );
};

export default BusinessManagement;